import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { apiClient } from "../api/client";
import { DEFAULT_THEME_ID, applyThemeToRoot, getTheme, isKnownTheme, listThemes } from ".";
import type { ThemeConfig } from "./types";

interface ThemeContextValue {
  theme: ThemeConfig;
  themes: ThemeConfig[];
  activeThemeIDs: ReadonlySet<string>;
  setTheme: (id: string) => void;
  refreshThemes: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const storageKeyFor = (userId: string | null): string => {
  return userId !== null && userId !== "" ? `blocks.theme.${userId}` : "blocks.theme.anon";
};

function readStoredTheme(userId: string | null): string {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_ID;
  }
  try {
    const stored = window.localStorage.getItem(storageKeyFor(userId));
    if (stored !== null && isKnownTheme(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable (privacy mode, SSR-like environment) — ignore.
  }
  return DEFAULT_THEME_ID;
}

function writeStoredTheme(userId: string | null, themeId: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(storageKeyFor(userId), themeId);
  } catch {
    // ignore — picker still works for the rest of the session.
  }
}

export function ThemeProvider({
  userId,
  children
}: {
  userId: string | null;
  children: React.ReactNode;
}) {
  const [themeId, setThemeId] = useState<string>(() => readStoredTheme(userId));
  // Set of theme IDs the tenant has marked active. `null` means "not yet
  // loaded" — until we know, every known theme is treated as active so the
  // picker doesn't briefly collapse to one option.
  const [activeIDs, setActiveIDs] = useState<Set<string> | null>(null);

  useEffect(() => {
    setThemeId(readStoredTheme(userId));
  }, [userId]);

  const fetchActive = useCallback(async () => {
    if (userId === null || userId === "") {
      setActiveIDs(null);
      return;
    }
    try {
      const response = await apiClient.listThemes();
      const next = new Set<string>();
      for (const entry of response.themes) {
        if (entry.is_active) {
          next.add(entry.id);
        }
      }
      setActiveIDs(next);
    } catch {
      // Failure leaves the picker showing all themes; the admin can still
      // recover by reloading. Logging is intentionally omitted — the api
      // client already surfaces a typed error.
    }
  }, [userId]);

  useEffect(() => {
    void fetchActive();
  }, [fetchActive]);

  // If the user's stored theme has been deactivated, fall back to an active
  // theme (preferring the default) and overwrite localStorage so the choice
  // doesn't keep getting rejected. If no theme is active — which the backend
  // disallows but we still defend against — keep the current selection so the
  // page stays rendered.
  useEffect(() => {
    if (activeIDs === null) {
      return;
    }
    if (activeIDs.has(themeId)) {
      return;
    }
    const fallback = activeIDs.has(DEFAULT_THEME_ID)
      ? DEFAULT_THEME_ID
      : listThemes().find((option) => activeIDs.has(option.id))?.id;
    if (fallback === undefined) {
      return;
    }
    setThemeId(fallback);
    writeStoredTheme(userId, fallback);
  }, [activeIDs, themeId, userId]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    applyThemeToRoot(getTheme(themeId), document.documentElement);
  }, [themeId]);

  const setTheme = useCallback(
    (next: string) => {
      if (!isKnownTheme(next)) {
        return;
      }
      setThemeId(next);
      writeStoredTheme(userId, next);
    },
    [userId]
  );

  const value = useMemo<ThemeContextValue>(() => {
    const allThemes = listThemes();
    const activeSet =
      activeIDs ?? new Set<string>(allThemes.map((option) => option.id));
    return {
      theme: getTheme(themeId),
      themes: allThemes,
      activeThemeIDs: activeSet,
      setTheme,
      refreshThemes: fetchActive
    };
  }, [setTheme, themeId, activeIDs, fetchActive]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === undefined) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return value;
}
